import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup, Badge, Tab, Nav, Modal, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'; // Added OverlayTrigger, Tooltip
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt,
  FaLock, FaUserCheck, FaIdCard, FaFileAlt, FaBuilding, FaUpload, FaCheck, FaTimesCircle,
  FaFileUpload, FaHistory, FaKey, FaEdit, FaUsers, FaDownload, FaUserCog, FaPercentage } from 'react-icons/fa'; // Added FaUserCog, FaPercentage
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import api, { rmApi, dsoApi, agentApi } from '../../services/api.js'; // Add agentApi
import Swal from 'sweetalert2';
import locationService from '../../services/location.js';
import './AddDSO.css'; // Reusing the same CSS

// Define Applicant Type Options for stability
const applicantTypeOptions = [
  { value: 'Individual', label: 'Individual' },
  { value: 'Business', label: 'Business' }
];

const EditDSO = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // State to control read-only mode (default to true for read-only)
  const [readOnly, setReadOnly] = useState(true);
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: 'male',
    address: '',
    state_id: null, // Use state_id
    city_id: null,  // Use city_id
    pincode_id: null, // Use pincode_id
    aadhaar_card_no: '', // Add Aadhaar number field
    applicantType: '',
    individualType: '',
    businessType: '',
    companyName: '',
    gstin: '',
    panNumber: '',
    relationshipManager: '',
    status: 'Active',
    // Add fields for S3 URLs
    adhaar_card_front_file: '', 
    adhaar_card_back_file: '',
    pan_card_file: '',
    company_certificate_file: '',
    company_pan_card_file: ''
  });
  
  // State for document uploads
  const [documents, setDocuments] = useState({
    aadhaar: {
      front: null,
      back: null,
      verified: false
    },
    pan: {
      file: null,
      verified: false
    },
    companyCertificate: {
      file: null,
      verified: false
    },
    companyPanCard: {
      file: null,
      verified: false
    }
  });
  
  const [validated, setValidated] = useState(false);
  // Removed showSuccess, showError, errorMessage states
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [isLoadingRm, setIsLoadingRm] = useState(false);
  // Add state for upload status
  const [uploading, setUploading] = useState({
    aadhaarFront: false,
    aadhaarBack: false,
    pan: false,
    companyCertificate: false,
    companyPanCard: false,
  });
  const [uploadError, setUploadError] = useState({
    aadhaarFront: null,
    aadhaarBack: null,
    pan: null,
    companyCertificate: null,
    companyPanCard: null,
  });
  
  // States, cities, and pincodes will be fetched from API
  const [states, setStates] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  
  // Individual Types
  const individualTypes = [
    'Salaried', 
    'Self Employed'
  ];

  // Business Types
  const businessTypes = [
    'Sole Proprietorship',
    'Partnership',
    'Limited Liability Partnership (LLP)',
    'Private Limited Company',
    'Public Limited Company',
    'One Person Company',
    'Non-Profit Organization',
    'Trust',
    'Hindu Undivided Family'
  ];
  
  // Cities and pincodes will be fetched based on selection
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  
  const [pincodes, setPincodes] = useState([]);
  const [isLoadingPincodes, setIsLoadingPincodes] = useState(false);

  // Add state for Agent Upload Modal
  const [showAgentUploadModal, setShowAgentUploadModal] = useState(false);
  const [selectedAgentFile, setSelectedAgentFile] = useState(null);
  const [agentUploadError, setAgentUploadError] = useState("");
  const [isUploadingAgents, setIsUploadingAgents] = useState(false);

  // Add state for Agent List Tab
  const [agentsList, setAgentsList] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState('');

  // Add state for Agent Reset Password Modal
  const [showAgentResetModal, setShowAgentResetModal] = useState(false);
  const [selectedAgentForReset, setSelectedAgentForReset] = useState(null);
  const [newAgentPassword, setNewAgentPassword] = useState('');
  const [showNewAgentPassword, setShowNewAgentPassword] = useState(false);
  const [agentPasswordStrength, setAgentPasswordStrength] = useState('');

  // Fetch DSO data on component mount
  useEffect(() => {
    // In a real app, this would be an API call to fetch DSO data by ID
    fetchDSOData();
    fetchRelationshipManagers();
    fetchStates();
    fetchAgentsList(); // Add this call
  }, [id]); // id is the dependency
  
  // Fetch DSO data from the API
  const fetchDSOData = async () => {
    try {
      const response = await dsoApi.getDsoById(id);
      const dsoData = response.data;
      const profile = dsoData.dso_profile || {}; // Get profile data safely

      // Map API response to form structure
      const formattedData = {
        id: dsoData.id,
        name: dsoData.name,
        email: dsoData.email,
        phone: dsoData.mobile,
        gender: dsoData.gender || 'male', // Assuming gender is top-level
        address: profile.address || '', // Use profile data
        state_id: profile.state_id ? parseInt(profile.state_id, 10) : null, // Use profile data and parse
        city_id: profile.city_id ? parseInt(profile.city_id, 10) : null, // Use profile data and parse
        pincode_id: profile.pincode_id ? parseInt(profile.pincode_id, 10) : null, // Use profile data and parse
        aadhaar_card_no: profile.adhaar_card_no || '', // Map Aadhaar number from profile
        applicantType: profile.applicant_type || '', // Use profile data
        businessType: profile.business_type || '', // Use profile data
        individualType: profile.individual_type || '', // Use profile data
        companyName: profile.company_name || '', // Use profile data
        gstin: profile.gstin || '', // Use profile data
        panNumber: profile.pan_card_no || '', // Corrected key: pan_card_no
        relationshipManager: profile.rm_id, // Use rm_id from profile
        // Use the status string directly from the API response profile
        status: profile.status || 'Pending Verification', // Use profile.status, default if missing
        verified_at: profile.is_verified || null, // Store the actual timestamp or null
        // Populate file URLs from profile
        adhaar_card_front_file: profile.adhaar_card_front_file || '',
        adhaar_card_back_file: profile.adhaar_card_back_file || '',
        pan_card_file: profile.pan_card_file || '',
        company_certificate_file: profile.company_certificate_file || '',
        company_pan_card_file: profile.company_pan_card_file || ''
      };
  
      setFormData(formattedData);
  
      // Helper to get filename from URL
      const extractFilename = (url) => url ? url.substring(url.lastIndexOf('/') + 1) : 'Uploaded File';
  
      // Initialize documents state based on the presence of URLs in formData
      const docData = {
        aadhaar: {
          front: formattedData.adhaar_card_front_file ? { name: extractFilename(formattedData.adhaar_card_front_file) } : null,
          back: formattedData.adhaar_card_back_file ? { name: extractFilename(formattedData.adhaar_card_back_file) } : null,
          verified: Boolean(profile.adhaar_verified) // Keep verification status if needed for UI
        },
        pan: {
          file: formattedData.pan_card_file ? { name: extractFilename(formattedData.pan_card_file) } : null,
          verified: profile.pan_verified !== null && profile.pan_verified !== undefined // Check if timestamp exists
        },
        companyCertificate: {
          file: formattedData.company_certificate_file ? { name: extractFilename(formattedData.company_certificate_file) } : null,
          verified: Boolean(profile.company_certificate_verified)
        },
        companyPanCard: {
          file: formattedData.company_pan_card_file ? { name: extractFilename(formattedData.company_pan_card_file) } : null,
          verified: Boolean(profile.company_pan_card_verified)
        }
      };
      setDocuments(docData);

      // Fetch location data based on the DSO's information
      if (formattedData.state_id) { // Use state_id
        await fetchCitiesByState(formattedData.state_id); // Use state_id
      }

      if (formattedData.city_id) { // Use city_id
        await fetchPincodesByCity(formattedData.city_id); // Use city_id
      }
    } catch (error) {
      console.error('Error fetching DSO data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: 'Failed to load DSO data. Please try again.'
      });
    }
  };
  
  const fetchRelationshipManagers = async () => {
    setIsLoadingRm(true);

    try {
      const response = await rmApi.getRelationshipManagers({
        limit: 100 // fetch all
      });

      const rmOptions = response.data.items.map(rm => ({
        value: rm.id,
        label: rm.name,
        email: rm.email,
        location: rm.location
      }));

      setRelationshipManagers(rmOptions);

    } catch (error) {
      console.error('Error fetching relationship managers:', error);
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: 'Failed to load relationship managers. Please try again.'
      });
    } finally {
      setIsLoadingRm(false);
    }
  };


  // Fetch states from API
  const fetchStates = async () => {
    setIsLoadingStates(true);
    try {
      const response = await locationService.getAllStates();
      const stateOptions = response.data.states.map(state => ({
        value: state.id,
        label: state.name,
        code: state.code
      }));
      setStates(stateOptions);
    } catch (error) {
      console.error('Error fetching states:', error);
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: 'Failed to fetch states. Please try again.'
      });
    } finally {
      setIsLoadingStates(false);
    }
  };

  // Fetch cities by state ID
  const fetchCitiesByState = async (stateId) => {
    if (!stateId) return;
    
    setIsLoadingCities(true);
    setCities([]);
    setPincodes([]);
    
    try {
      const response = await locationService.getCitiesByState(stateId);
      const cityOptions = response.data.cities.map(city => ({
        value: city.id,
        label: city.name
      }));
      setCities(cityOptions);
    } catch (error) {
      console.error('Error fetching cities:', error);
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: 'Failed to fetch cities for the selected state. Please try again.'
      });
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Fetch pincodes by city ID
  const fetchPincodesByCity = async (cityId) => {
    if (!cityId) return;
    
    setIsLoadingPincodes(true);
    setPincodes([]);
    
    try {
      const response = await locationService.getPincodesByCity(cityId);
      const pincodeOptions = response.data.pincodes.map(pincode => ({
        value: pincode.id,
        label: pincode.code
      }));
      setPincodes(pincodeOptions);
    } catch (error) {
      console.error('Error fetching pincodes:', error);
      Swal.fire({
        icon: 'error',
        title: 'Load Failed',
        text: 'Failed to fetch pincodes for the selected city. Please try again.'
      });
    } finally {
      setIsLoadingPincodes(false);
    }
  };

  // Update cities when state changes
  const handleStateChange = (selectedOption) => {
    const newStateId = selectedOption ? selectedOption.value : null; // Get ID or null
    setFormData(prev => ({
      ...prev,
      state_id: newStateId, // Update state_id
      city_id: null, // Reset city_id
      pincode_id: null // Reset pincode_id
    }));

    setCities([]); // Clear cities immediately
    setPincodes([]); // Clear pincodes immediately

    if (newStateId) {
      fetchCitiesByState(newStateId);
    }
  };

  // Update pincodes when city changes
  const handleCityChange = (selectedOption) => {
    const newCityId = selectedOption ? selectedOption.value : null; // Get ID or null
    setFormData(prev => ({
      ...prev,
      city_id: newCityId, // Update city_id
      pincode_id: null // Reset pincode_id
    }));

    setPincodes([]); // Clear pincodes immediately

    if (newCityId) {
      fetchPincodesByCity(newCityId);
    }
  };
  
  // Handle applicant type change
  const handleApplicantTypeChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      applicantType: value,
      individualType: '',
      businessType: ''
    });
    
    // Reset document state based on the new applicant type
    if (value === 'Individual') {
      setDocuments({
        aadhaar: {
          front: null,
          back: null,
          verified: false
        },
        pan: {
          file: null,
          verified: false
        },
        companyCertificate: {
          file: null,
          verified: false
        },
        companyPanCard: {
          file: null,
          verified: false
        }
      });
    } else if (value === 'Business') {
      setDocuments({
        aadhaar: {
          front: null,
          back: null,
          verified: false
        },
        pan: {
          file: null,
          verified: false
        },
        companyCertificate: {
          file: null,
          verified: false
        },
        companyPanCard: {
          file: null,
          verified: false
        }
      });
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle react-select change for relationship manager
  const handleRMChange = (selectedOption) => {
    setFormData({
      ...formData,
      relationshipManager: selectedOption ? selectedOption.value : ''
    });
  };
  
  // Handle document upload
  const handleDocumentUpload = async (e, docType, subType = null) => { // Add async keyword here
    const file = e.target.files[0];
    if (!file) return;
    
    const uploadKey = subType ? `${docType}${subType.charAt(0).toUpperCase() + subType.slice(1)}` : docType;
    const formDataKey = subType ? `${docType}_${subType}_file` : `${docType}_file`;
    
    // Adjust formDataKey for specific backend field names AND define backend document_type string
    let backendFieldName = formDataKey;
    let backendDocumentType = ''; // Variable for the query parameter

    if (docType === 'aadhaar' && subType === 'front') {
        backendFieldName = 'adhaar_card_front_file';
        backendDocumentType = 'aadhaar_front';
    } else if (docType === 'aadhaar' && subType === 'back') {
        backendFieldName = 'adhaar_card_back_file';
        backendDocumentType = 'aadhaar_back';
    } else if (docType === 'pan') {
        backendFieldName = 'pan_card_file';
        backendDocumentType = 'pan';
    } else if (docType === 'companyCertificate') {
        backendFieldName = 'company_certificate_file';
        backendDocumentType = 'company_certificate';
    } else if (docType === 'companyPanCard') {
        backendFieldName = 'company_pan_card_file';
        backendDocumentType = 'company_pan_card';
    } else {
        console.error("Invalid document type combination:", docType, subType);
        setUploadError(prev => ({ ...prev, [uploadKey]: "Invalid document type specified." }));
        return; // Stop if type is invalid
    }


    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    setUploadError(prev => ({ ...prev, [uploadKey]: null }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      // Construct the URL with dso_id and document_type query parameter
      // 'id' should be available from useParams() at the top of the component
      const uploadUrl = `/admin/dso/${id}/upload-document?document_type=${encodeURIComponent(backendDocumentType)}`;
      
      // Use the generic 'api' instance which should be configured for authenticated requests
      const response = await api.post(uploadUrl, uploadFormData, { // Use the new URL
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { url } = response.data; // URL comes from the response

      // Update formData with the returned S3 URL
      setFormData(prev => ({
        ...prev,
        [backendFieldName]: url
      }));

      // Update documents state to show the uploaded file name
      const newDocState = { ...documents };
      if (subType) {
        newDocState[docType][subType] = { name: file.name }; // Store original filename for display
      } else {
        newDocState[docType].file = { name: file.name };
      }
      setDocuments(newDocState);

    } catch (error) {
      console.error(`Error uploading ${uploadKey}:`, error);
      const errorMsg = error.response?.data?.detail || `Failed to upload ${file.name}. Please try again.`;
      setUploadError(prev => ({ ...prev, [uploadKey]: errorMsg }));
      // Clear the file input if needed
      e.target.value = null; 
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };
   
  // Handle document verification - Now makes API call
  const handleDocumentVerify = async (docType, verified) => {
    // Only handle PAN verification for now, extend if needed for others
    if (docType !== 'pan' && docType !== 'companyPanCard') {
      console.warn(`Verification logic not implemented for type: ${docType}`);
      // Optionally update local state for other types if needed temporarily
      // setDocuments(prev => ({
      //   ...prev,
      //   [docType]: { ...prev[docType], verified: verified }
      // }));
      return;
    }

    const payload = {
      // Send pan_verified within profile_data as expected by the backend update logic
      profile_data: {
        [docType === 'pan' ? 'pan_verified' : 'company_pan_card_verified']: verified // Send boolean, backend converts to timestamp/null
      }
    };

    try {
      console.log(`Attempting to ${verified ? 'approve' : 'disapprove'} ${docType === 'pan' ? 'PAN' : 'Company PAN'} for DSO ID: ${id}`);
      // Use the existing updateDso API endpoint
      const response = await dsoApi.updateDso(id, payload);

      // Update local state based on successful API call
      setDocuments(prev => ({
        ...prev,
        [docType]: {
          ...prev[docType],
          // Update based on the response profile's pan_verified timestamp
          verified: response.data?.dso_profile?.[docType === 'pan' ? 'pan_verified' : 'company_pan_card_verified'] !== null && response.data?.dso_profile?.[docType === 'pan' ? 'pan_verified' : 'company_pan_card_verified'] !== undefined
        }
      }));

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `${docType === 'pan' ? 'PAN card' : 'Company PAN card'} ${verified ? 'approved' : 'status reset'} successfully!`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });

    } catch (error) {
      console.error(`Error updating ${docType === 'pan' ? 'PAN' : 'Company PAN'} verification status:`, error);
      const errorMsg = error.response?.data?.detail || `Failed to update ${docType === 'pan' ? 'PAN' : 'Company PAN'} status.`;
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMsg
      });
    }
  };
   
  // Generate password strength and check functions (same as AddDSO)
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength('');
      return;
    }
    
    let strength = 0;
    
    // Length check
    if (password.length >= 5) strength += 1;
    
    // Contains uppercase letters
    if (password.match(/[A-Z]/)) strength += 1;
    
    // Contains lowercase letters
    if (password.match(/[a-z]/)) strength += 1;
    
    // Contains numbers
    if (password.match(/[0-9]/)) strength += 1;
    
    switch (strength) {
      case 0:
      case 1:
        setPasswordStrength('weak');
        break;
      case 2:
        setPasswordStrength('medium');
        break;
      case 3:
      case 4:
        setPasswordStrength('strong');
        break;
      default:
        setPasswordStrength('');
    }
  };
  
  // Get password strength color
  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'strong':
        return 'success';
      default:
        return 'secondary';
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    try {
      // Call API to reset the password
      await dsoApi.resetDsoPassword(id, newPassword);
      
      console.log(`Password reset successful for DSO ID: ${id}`);
      
      // Show success toast
      setShowResetPasswordModal(false);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Password reset successfully!',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
      setNewPassword('');

    } catch (error) {
      console.error('Error resetting password:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      // Show error modal
      Swal.fire({
        icon: 'error',
        title: 'Password Reset Failed',
        text: errorMsg
      });
      setShowResetPasswordModal(false);
    }
  };
  
  // Handle verify DSO
  const handleVerifyDSO = async () => {
    try {
      // Call API to verify the DSO
      const response = await dsoApi.updateDso(id, { 
        is_verified: true 
      });
      
      console.log(`DSO ID: ${id} verified successfully:`, response.data);
      
      // Update status and verification timestamp in form data
      setFormData(prevData => ({
        ...prevData,
        status: 'Verified', // Update status string
        verified_at: response.data?.dso_profile?.is_verified || new Date().toISOString() // Store timestamp from response or estimate
      }));

      // Close modal and show success toast
      setShowVerifyModal(false);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'DSO verified successfully!',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });

    } catch (error) {
      console.error('Error verifying DSO:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to verify DSO. Please try again.';
      // Show error modal
      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: errorMsg
      });
      setShowVerifyModal(false);
    }
  };

  const handleSubmit = async (e) => {
    console.log('handleSubmit triggered!'); // Debug log
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    try {
      // Format data for API - Assuming profile data needs nesting
      const updateData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.phone,
        gender: formData.gender, // Assuming top-level
        // Nest profile data - adjust 'profile_data' key if needed based on API spec
        profile_data: {
            address: formData.address,
            state_id: formData.state_id, // Send state_id
            city_id: formData.city_id,   // Send city_id
            pincode_id: formData.pincode_id, // Send pincode_id
            rm_id: formData.relationshipManager, // Ensure this key matches backend (likely rm_id)
            applicant_type: formData.applicantType,
            individual_type: formData.individualType,
            business_type: formData.businessType,
            company_name: formData.companyName,
            gstin: formData.gstin, // Ensure this key matches backend (likely gstin_no)
            pan_card_no: formData.panNumber, // Ensure this key matches backend (likely pan_card_no)
            adhaar_card_no: formData.aadhaar_card_no, // Ensure this key matches backend (likely adhaar_card_no)
            // Include S3 URLs from formData (ensure these are up-to-date if uploads happened without saving)
            adhaar_card_front_file: formData.adhaar_card_front_file,
            adhaar_card_back_file: formData.adhaar_card_back_file,
            pan_card_file: formData.pan_card_file,
            company_certificate_file: formData.company_certificate_file,
            company_pan_card_file: formData.company_pan_card_file
            // DO NOT send 'is_verified' here - verification is a separate action
        }
        // DO NOT send top-level 'is_verified' here either
      };

      console.log('Updating DSO data:', updateData);

      // Make API call to update DSO
      // Ensure the endpoint and payload structure match the backend expectation
      const response = await dsoApi.updateDso(id, updateData); // Pass the structured data

      console.log('DSO updated successfully:', response.data);
      
      // Show success toast
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'DSO updated successfully!',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
      
      // Switch back to read-only mode after successful update
      setReadOnly(true);
      
    } catch (error) {
      console.error('Error updating DSO:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update DSO. Please try again.';
      // Show error modal
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMsg
      });
    }
  };

  // Fetch agents list for the current DSO
  const fetchAgentsList = async () => {
    if (!id) return; // Don't fetch if DSO ID isn't available

    setIsLoadingAgents(true);
    setAgentsError('');
    try {
      // Use the getAgentsForDso function from dsoApi
      // Assuming dsoApi.getAgentsForDso exists and takes dsoId
      const response = await dsoApi.getAgentsForDso(id); // Use high limit default
      setAgentsList(response.data.items || []);
    } catch (error) {
      // Log the full error for detailed debugging
      console.error('Error fetching agents list:', error.response || error.message || error);

      // Extract a more specific error message if available from the backend response
      let detailedErrorMsg = 'Failed to load agents list. Please check console for details.';
      if (error.response) {
        // Use detail field if present, otherwise stringify the data
        detailedErrorMsg = error.response.data?.detail || JSON.stringify(error.response.data) || error.response.statusText;
      } else if (error.message) {
        detailedErrorMsg = error.message;
      }

      setAgentsError(detailedErrorMsg); // Set the more specific error message

      // Optionally use Swal for consistency, showing the detailed error
      // Swal.fire({ icon: 'error', title: 'Load Failed', text: detailedErrorMsg });
    } finally {
      setIsLoadingAgents(false);
    }
  };

  // Function to open the agent upload modal
  const openAgentUploadModal = () => {
    setSelectedAgentFile(null); // Reset file selection
    setAgentUploadError(""); // Clear previous errors
    setShowAgentUploadModal(true);
  };

  // Handle file selection for agent upload
  const handleAgentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Basic validation (optional: check file type/size)
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setAgentUploadError('Invalid file type. Please upload a CSV file.');
        setSelectedAgentFile(null);
      } else {
        setSelectedAgentFile(file);
        setAgentUploadError(''); // Clear error on valid selection
      }
    } else {
      setSelectedAgentFile(null);
    }
  };

  // Function to download CSV template (copied from DSOList)
  const downloadTemplate = () => {
    // Adding sample data row and ensuring the last line has a newline character
    const csvContent = "name,mobile,email\nAgent Name,9999999999,agent@example.com\n"; 
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "agent_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle the actual agent upload process (adapted from previous instructions)
  const handleAgentUpload = async () => {
    if (!selectedAgentFile) {
      setAgentUploadError('Please select a CSV file to upload.');
      return;
    }

    setIsUploadingAgents(true);
    setAgentUploadError('');

    const uploadData = new FormData();
    uploadData.append('file', selectedAgentFile);

    try {
      // Use the new dsoApi.bulkUploadAgents function
      // 'id' is available from useParams()
      const response = await dsoApi.bulkUploadAgents(id, uploadData);

      console.log('Agent upload response:', response.data);
      setShowAgentUploadModal(false);
      
      // Create a more detailed message with the upload results
      const result = response.data;
      const resultMessage = `
        Total rows processed: ${result.total_records}
        Successfully imported: ${result.successful_imports}
        Skipped (already exist): ${result.skipped_existing || 0}
        Failed: ${result.failed_imports}
      `;
      
      Swal.fire({
        icon: 'success',
        title: 'Upload Completed',
        html: `<div class="text-start">
          <p>${result.message || `Processed agents for ${formData.name}.`}</p>
          <pre>${resultMessage}</pre>
        </div>`,
      });
      // Fetch updated agent list
      fetchAgentsList();

    } catch (error) {
      console.error('Error uploading agents:', error);
      const errorDetail = error.response?.data?.detail || 'An unexpected error occurred during upload.';
      let errorText = errorDetail;
      // Attempt to parse complex error structures from backend validation
      if (typeof errorDetail === 'object' && errorDetail.errors && Array.isArray(errorDetail.errors) && errorDetail.errors.length > 0) {
         const errorMessages = errorDetail.errors.map(err => `Row ${err.row}: ${err.error}`).join('<br>');
         errorText = `Upload failed:<br>${errorMessages}`;
         console.error("Agent Upload Errors:", errorDetail.errors);
         // Display detailed errors using Swal
         Swal.fire({
           icon: 'error',
           title: 'Upload Failed',
           html: errorText, // Use html property for line breaks
           confirmButtonText: 'Close'
         });
         // Also set the error in the modal for reference if needed
         setAgentUploadError(`Upload failed with ${errorDetail.errors.length} errors. See details above or in console.`);
      } else if (typeof errorDetail === 'object') {
         // Handle other object-based errors
         errorText = JSON.stringify(errorDetail);
         setAgentUploadError(errorText);
         Swal.fire({ icon: 'error', title: 'Upload Failed', text: errorText });
      } else {
         // Handle simple string errors
         setAgentUploadError(errorText);
         Swal.fire({ icon: 'error', title: 'Upload Failed', text: errorText });
      }
    } finally {
      setIsUploadingAgents(false);
    }
  };

  // Open agent password reset modal
  const openAgentResetModal = (agent) => {
    setSelectedAgentForReset(agent);
    setNewAgentPassword('');
    setAgentPasswordStrength('');
    setShowNewAgentPassword(false);
    setShowAgentResetModal(true);
  };

  // Check agent password strength (reuse existing function logic)
  const checkAgentPasswordStrength = (password) => {
     // This function is identical to checkPasswordStrength, just sets a different state variable
     if (!password) {
       setAgentPasswordStrength('');
       return;
     }
     let strength = 0;
     if (password.length >= 5) strength += 1;
     if (password.match(/[A-Z]/)) strength += 1;
     if (password.match(/[a-z]/)) strength += 1;
     if (password.match(/[0-9]/)) strength += 1;

     switch (strength) {
       case 0: case 1: setAgentPasswordStrength('weak'); break;
       case 2: setAgentPasswordStrength('medium'); break;
       case 3: case 4: setAgentPasswordStrength('strong'); break;
       default: setAgentPasswordStrength('');
     }
  };

  // Get agent password strength color (reuse existing function logic)
  const getAgentPasswordStrengthColor = () => {
     // This function is identical to getPasswordStrengthColor, just uses a different state variable
     switch (agentPasswordStrength) {
       case 'weak': return 'danger';
       case 'medium': return 'warning';
       case 'strong': return 'success';
       default: return 'secondary';
     }
  };

  // Handle agent password reset API call
  const handleAgentPasswordReset = async () => {
    if (!selectedAgentForReset || !newAgentPassword || agentPasswordStrength !== 'strong') {
      Swal.fire({ icon: 'warning', title: 'Invalid Input', text: 'Please enter a strong password.' });
      return;
    }

    try {
      // Use the agentApi function - Assuming agentApi.resetAgentPassword exists
      // Need to define agentApi in api.js first
      await agentApi.resetAgentPassword(selectedAgentForReset.id, newAgentPassword);
      setShowAgentResetModal(false);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `Password reset successfully for ${selectedAgentForReset.name}!`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });
    } catch (error) {
      console.error('Error resetting agent password:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reset agent password.';
      Swal.fire({ icon: 'error', title: 'Reset Failed', text: errorMsg });
      // Keep modal open on failure? Or close? Let's close it.
      // setShowAgentResetModal(false);
    }
  };

  return (
    <Container fluid className="p-3">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <h4 className="mb-1">
            <FaArrowLeft 
              className="me-2 cursor-pointer" 
              onClick={() => navigate('/admin/dso-list')}
              style={{ cursor: 'pointer' }}
            />
            View/Edit DSO
          </h4>
          <p className="text-muted small mb-0">Manage DSO partner details and documents</p>
        </Col>
      </Row>
      
      {/* Status Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body className="py-2">
              <Row>
                <Col md={12}>
                  <div className="d-flex align-items-center">
                    <FaCheck className="text-muted me-2" />
                    <div className="text-muted small me-2">Status:</div>
                    <Badge 
                      bg={
                        formData.status === 'Deactivated' ? 'secondary' :
                        formData.status === 'Verified' ? 'success' : 'warning'
                      }
                    >
                      {formData.status}
                      {/* Optionally display verification date */}
                      {/* {formData.status === 'Verified' && formData.verified_at && 
                        ` (on ${new Date(formData.verified_at).toLocaleDateString()})`} */}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="d-flex justify-content-end align-items-center">
          {/* Show Verify button only if active and not yet verified */}
          {formData.status === 'Pending Verification' && (
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={() => setShowVerifyModal(true)}
            >
              <FaCheck className="me-1" /> Verify
            </Button>
          )}
          
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="me-2"
            onClick={() => setShowResetPasswordModal(true)}
          >
            <FaKey className="me-1" /> Reset Password
          </Button>
          {/* Add Commission Management Button */}
          <Button
            variant="outline-success"
            size="sm"
            className="me-2"
            onClick={() => navigate(`/admin/dso/${id}/commissions`)}
          >
            <FaPercentage className="me-1" /> Manage Commissions
          </Button>
          {/* Add Upload Agents Button - Show only if DSO is Verified */}
          {formData.status === 'Verified' && (
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => openAgentUploadModal()} // Call handler to open modal
              >
                <FaUsers className="me-1" /> Upload Agents
              </Button>
          )}
        </Col>
      </Row>
      
      {/* Success and Error Alerts Removed */}
      
      <Tab.Container defaultActiveKey="details">
        <Row>
          <Col md={3} lg={2} className="mb-3">
            <Nav variant="pills" className="flex-column shadow-sm">
              <Nav.Item>
                <Nav.Link eventKey="details" className="d-flex align-items-center">
                  <FaUser className="me-2" /> Details
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents" className="d-flex align-items-center">
                  <FaFileAlt className="me-2" /> Documents
                </Nav.Link>
              </Nav.Item>
              {/* Add Agents Tab Link */}
              <Nav.Item>
                <Nav.Link eventKey="agents" className="d-flex align-items-center">
                  <FaUsers className="me-2" /> Mapped Agents
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          
          <Col md={9} lg={10}>
            <Card className="shadow-sm">
              <Card.Body>
                <Tab.Content>
                  {/* Details Tab */}
                  <Tab.Pane eventKey="details">
                    <Form noValidate validated={validated} onSubmit={handleSubmit} className="compact-form">
                      {/* Applicant Type Section */}
                      <div className="applicant-section">
                        <Row className="g-2">
                          <Col md={4}>
                            <Form.Group>
                              <Select
                                name="applicantType"
                                value={applicantTypeOptions.find(option => option.value === formData.applicantType) || null}
                                onChange={(selectedOption) => {
                                  handleApplicantTypeChange({ target: { value: selectedOption ? selectedOption.value : '' } });
                                }}
                                options={applicantTypeOptions}
                                placeholder="Select Applicant Type"
                                className={validated && !formData.applicantType ? 'is-invalid' : ''}
                                classNamePrefix="react-select"
                                isClearable={false}
                                isDisabled={readOnly}
                              />
                              {validated && !formData.applicantType && (
                                <div className="invalid-feedback" style={{display: 'block'}}>
                                  Please select applicant type.
                                </div>
                              )}
                            </Form.Group>
                          </Col>

                          {formData.applicantType === 'Individual' && (
                            <Col md={4}>
                              <Form.Group>
                                <Select
                                  name="individualType"
                                  value={formData.individualType ? { value: formData.individualType, label: formData.individualType } : null}
                                  onChange={(selectedOption) => {
                                    handleInputChange({ target: { name: 'individualType', value: selectedOption ? selectedOption.value : '' } });
                                  }}
                                  options={individualTypes.map(type => ({ value: type, label: type }))}
                                  placeholder="Select Individual Type"
                                  className={validated && !formData.individualType ? 'is-invalid' : ''}
                                  classNamePrefix="react-select"
                                  isClearable={false}
                                  isDisabled={readOnly}
                                />
                                {validated && !formData.individualType && (
                                  <div className="invalid-feedback" style={{display: 'block'}}>
                                    Please select individual type.
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          )}

                          {formData.applicantType === 'Business' && (
                            <Col md={4}>
                              <Form.Group>
                                <Select
                                  name="businessType"
                                  value={formData.businessType ? { value: formData.businessType, label: formData.businessType } : null}
                                  onChange={(selectedOption) => {
                                    handleInputChange({ target: { name: 'businessType', value: selectedOption ? selectedOption.value : '' } });
                                  }}
                                  options={businessTypes.map(type => ({ value: type, label: type }))}
                                  placeholder="Select Business Type"
                                  className={validated && !formData.businessType ? 'is-invalid' : ''}
                                  classNamePrefix="react-select"
                                  isClearable={false}
                                  isDisabled={readOnly}
                                />
                                {validated && !formData.businessType && (
                                  <div className="invalid-feedback" style={{display: 'block'}}>
                                    Please select business type.
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          )}
                        </Row>
                      </div>

                      {/* Relationship Manager Section */}
                      <Row className="mb-3">
                        <Col>
                          <h6 className="section-header text-primary border-bottom">
                            <FaUserCheck className="me-2" /> Relationship Manager
                          </h6>
                          <Form.Group>
                            <Form.Label>Select Relationship Manager <span className="text-danger">*</span></Form.Label>
                            <Select
                              options={relationshipManagers}
                              isLoading={isLoadingRm}
                              isSearchable={true}
                              isClearable={true}
                              placeholder="Search and select a relationship manager..."
                              onChange={handleRMChange}
                              value={relationshipManagers.find(rm => rm.value === formData.relationshipManager)}
                              className={
                                validated && !formData.relationshipManager 
                                ? 'is-invalid' 
                                : ''
                              }
                              formatOptionLabel={option => (
                                <div className="d-flex flex-column">
                                  <div>{option.label}</div>
                                  <small className="text-muted">
                                   {option.email} • {option.location}
                                 </small>
                                </div>
                              )}
                              noOptionsMessage={() => "No relationship managers found"}
                              loadingMessage={() => "Loading relationship managers..."}
                              isDisabled={readOnly}
                            />
                            {validated && !formData.relationshipManager && (
                              <div className="invalid-feedback" style={{display: 'block'}}>
                                Please select a relationship manager.
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      {/* Two Column Layout for the Rest of the Form */}
                      <Row className="g-3">
                        <Col md={6}>
                          {/* Personal Information Section */}
                          <h6 className="section-header text-primary border-bottom">
                            <FaUser className="me-2" /> Personal Information
                          </h6>
                          
                          <Form.Group className="mb-3">
                            <InputGroup>
                              <InputGroup.Text><FaUser /></InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Full Name"
                                required
                                readOnly={readOnly}
                                disabled={readOnly}
                              />
                              <Form.Control.Feedback type="invalid">
                                Please provide a name.
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                          
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Gender</Form.Label>
                                <div className="d-flex">
                                  <Form.Check
                                    type="radio"
                                    label="Male"
                                    name="gender"
                                    id="gender-male"
                                    value="male"
                                    checked={formData.gender === 'male'}
                                    onChange={handleInputChange}
                                    className="me-4"
                                    disabled={readOnly}
                                  />
                                  <Form.Check
                                    type="radio"
                                    label="Female"
                                    name="gender"
                                    id="gender-female"
                                    value="female"
                                    checked={formData.gender === 'female'}
                                    onChange={handleInputChange}
                                    disabled={readOnly}
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                          
                          <Form.Group className="mb-3">
                            <InputGroup>
                              <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Email Address"
                                required
                                readOnly={readOnly}
                                disabled={readOnly}
                              />
                              <Form.Control.Feedback type="invalid">
                                Please provide a valid email address.
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <InputGroup>
                              <InputGroup.Text><FaPhone /></InputGroup.Text>
                              <Form.Control
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="Phone Number (10 digits)"
                                pattern="[0-9]{10}"
                                required
                                readOnly={readOnly}
                                disabled={readOnly}
                              />
                              <Form.Control.Feedback type="invalid">
                                Please provide a valid 10-digit phone number.
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                          
                          {/* Location Information Section */}
                          <h6 className="section-header text-primary border-bottom">
                            <FaMapMarkerAlt className="me-2" /> Location
                          </h6>
                          
                          <Form.Group className="mb-3">
                            <InputGroup>
                              <InputGroup.Text><FaMapMarkerAlt /></InputGroup.Text>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                placeholder="Full Address"
                                required
                                readOnly={readOnly}
                                disabled={readOnly}
                              />
                              <Form.Control.Feedback type="invalid">
                                Please provide an address.
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                          
                          <Form.Group className="mb-2">
                            <Select
                              name="state_id" // Use state_id
                              value={states.find(option => option.value === formData.state_id) || null} // Use state_id
                              onChange={handleStateChange} // Ensure handleStateChange updates state_id
                              options={states}
                              isLoading={isLoadingStates}
                              placeholder="Select State"
                              className={validated && !formData.state_id ? 'is-invalid' : ''} // Check state_id
                              classNamePrefix="react-select"
                              isClearable
                              isDisabled={readOnly}
                            />
                            {validated && !formData.state_id && ( // Check state_id
                              <div className="invalid-feedback" style={{display: 'block'}}>
                                Please select a state.
                              </div>
                            )}
                          </Form.Group>
                          
                          <Row className="g-2">
                            <Col md={6}>
                              <Form.Group>
                                <Select
                                  name="city_id" // Use city_id
                                  value={cities.find(option => option.value === formData.city_id) || null} // Use city_id
                                  onChange={handleCityChange} // Ensure handleCityChange updates city_id
                                  options={cities}
                                  placeholder="Select City"
                                  isDisabled={readOnly || !formData.state_id} // Check state_id
                                  isLoading={isLoadingCities}
                                  className={validated && !formData.city_id ? 'is-invalid' : ''} // Check city_id
                                  classNamePrefix="react-select"
                                  isClearable
                                />
                                {validated && !formData.city_id && ( // Check city_id
                                  <div className="invalid-feedback" style={{display: 'block'}}>
                                    Please select a city.
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            
                            <Col md={6}>
                              <Form.Group>
                                <Select
                                  name="pincode_id" // Use pincode_id
                                  value={pincodes.find(option => option.value === formData.pincode_id) || null} // Use pincode_id
                                  onChange={(selectedOption) => {
                                    // Update pincode_id directly
                                    handleInputChange({ target: { name: 'pincode_id', value: selectedOption ? selectedOption.value : '' } });
                                  }}
                                  options={pincodes}
                                  placeholder="Select Pincode"
                                  isDisabled={readOnly || !formData.city_id} // Check city_id
                                  isLoading={isLoadingPincodes}
                                  className={validated && !formData.pincode_id ? 'is-invalid' : ''} // Check pincode_id
                                  classNamePrefix="react-select"
                                  isClearable
                                />
                                {validated && !formData.pincode_id && ( // Check pincode_id
                                  <div className="invalid-feedback" style={{display: 'block'}}>
                                    Please select a pincode.
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Col>
                        
                        <Col md={6}>
                          {/* Identification Section */}
                          <h6 className="section-header text-primary border-bottom">
                            <FaIdCard className="me-2" /> Identification
                          </h6>

                          <Form.Group className="mb-3">
                            <InputGroup>
                              <InputGroup.Text><FaIdCard /></InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="panNumber"
                                value={formData.panNumber}
                                onChange={handleInputChange}
                                placeholder="PAN Number"
                                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                required
                                readOnly={readOnly}
                                disabled={readOnly}
                              />
                              <Form.Control.Feedback type="invalid">
                                Please provide a valid PAN number (e.g., ABCDE1234F).
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>

                          {/* Aadhaar Card Number Input */}
                          <Form.Group className="mb-3">
                            <InputGroup>
                              <InputGroup.Text><FaIdCard /></InputGroup.Text>
                              <Form.Control
                                type="text"
                                name="aadhaar_card_no"
                                value={formData.aadhaar_card_no}
                                onChange={handleInputChange}
                                placeholder="Aadhaar Card Number"
                                pattern="[0-9]{12}" // Basic 12-digit validation
                                required // Make required if necessary
                                readOnly={readOnly}
                                disabled={readOnly}
                                maxLength="12" // <-- Add this line
                              />
                              <Form.Control.Feedback type="invalid">
                                Please provide a valid Aadhaar number.
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>

                          {/* Status Section */}
                          <h6 className="section-header text-primary border-bottom">
                            <FaCheck className="me-2" /> Status Information
                          </h6>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                              name="status"
                              value={formData.status} // Display current status
                              disabled // Always disable direct status changes
                              readOnly // Make readOnly visually
                              style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }} // Add styling for read-only appearance
                            >
                              {/* Options should match the possible values for formData.status */}
                              <option value="Verified">Verified</option>
                              <option value="Pending Verification">Pending Verification</option>
                              <option value="Deactivated">Deactivated</option>
                            </Form.Select>
                          </Form.Group>
                          
                          
                          {formData.applicantType === 'Business' && (
                            <>
                              <h6 className="section-header text-primary border-bottom mt-4">
                                <FaBuilding className="me-2" /> Business Information
                              </h6>
                              
                              <Form.Group className="mb-3">
                                <Form.Label>Company Name</Form.Label>
                                <InputGroup>
                                  <InputGroup.Text><FaBuilding /></InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    placeholder="Company Name"
                                    required={formData.applicantType === 'Business'}
                                    readOnly={readOnly}
                                    disabled={readOnly}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Please provide company name.
                                  </Form.Control.Feedback>
                                </InputGroup>
                              </Form.Group>
                              
                              <Form.Group className="mb-3">
                                <Form.Label>GSTIN Number</Form.Label>
                                <InputGroup>
                                  <InputGroup.Text><FaIdCard /></InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    name="gstin"
                                    value={formData.gstin}
                                    onChange={handleInputChange}
                                    placeholder="GSTIN Number"
                                    required={formData.applicantType === 'Business'}
                                    readOnly={readOnly}
                                    disabled={readOnly}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    Please provide valid GSTIN number.
                                  </Form.Control.Feedback>
                                </InputGroup>
                              </Form.Group>
                            </>
                          )}
                        </Col>
                      </Row>
                      
                      <hr className="my-3" />
                      
                      <Row>
                        <Col className="d-flex gap-2 action-buttons">
                          {readOnly ? (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => { // Add event parameter 'e'
                                e.preventDefault(); // Prevent default button behavior
                                e.stopPropagation(); // Stop event bubbling
                                console.log('Edit button clicked, setting readOnly=false'); // Debug log
                                setReadOnly(false);
                              }}
                            >
                              <FaEdit className="me-1" /> Edit
                            </Button>
                          ) : (
                            <Button 
                              variant="primary" 
                              type="submit"
                              size="sm"
                            >
                              <FaSave className="me-1" /> Save Changes
                            </Button>
                          )}
                          
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => navigate('/admin/dso-list')}
                            size="sm"
                          >
                            Back to List
                          </Button>
                          
                          {!readOnly && (
                            <Button 
                              variant="outline-secondary" 
                              size="sm" 
                              onClick={() => setReadOnly(true)}
                            >
                              Cancel
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Form>
                  </Tab.Pane>
                  
                  {/* Documents Tab */}
                  <Tab.Pane eventKey="documents">
                    <h6 className="section-header text-primary border-bottom">
                      <FaFileAlt className="me-2" /> Document Uploads
                    </h6>
                    
                    <Row className="mt-3">
                      {formData.applicantType === 'Individual' && (
                        <>
                          {/* Aadhaar Card Upload - Only for Individual */}
                          <Col md={6} className="mb-4">
                            <Card className="h-100 shadow-sm">
                              <Card.Header className="bg-light py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0 d-flex align-items-center">
                                    <FaIdCard className="me-2 text-primary" /> Aadhaar Card
                                  </h6>
                                  
                                </div>
                              </Card.Header>
                              <Card.Body>
                                <Row className="g-2">
                                  <Col xs={12}>
                                    <Form.Group className="mb-3">
                                      <Form.Label>Front Side</Form.Label>
                                      <InputGroup>
                                        <Form.Control 
                                          type="file" 
                                          onChange={(e) => handleDocumentUpload(e, 'aadhaar', 'front')}
                                          accept="image/*,.pdf"
                                          disabled={uploading.aadhaarFront}
                                          isInvalid={!!uploadError.aadhaarFront}
                                        />
                                        {/* Keep the button for visual consistency, but disable/show loading */}
                                        <Button variant="outline-primary" disabled={uploading.aadhaarFront}>
                                          {uploading.aadhaarFront ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                          ) : (
                                            <FaUpload className="me-1" />
                                          )}
                                          {uploading.aadhaarFront ? ' Uploading...' : ' Upload'}
                                        </Button>
                                        <Form.Control.Feedback type="invalid">
                                          {uploadError.aadhaarFront}
                                        </Form.Control.Feedback>
                                      </InputGroup>
                                      {/* Show uploaded file name or link to existing file */}
                                      {!uploading.aadhaarFront && !uploadError.aadhaarFront && (
                                        formData.adhaar_card_front_file ? (
                                          <div className="mt-2 small">
                                            <FaCheck className="me-1 text-success" />
                                            <a href={formData.adhaar_card_front_file} target="_blank" rel="noopener noreferrer">
                                              View Uploaded Front
                                            </a>
                                            <span className="text-muted ms-2">({documents.aadhaar.front?.name || 'Previously uploaded'})</span>
                                          </div>
                                        ) : documents.aadhaar.front && (
                                          // Show only if a new file was just selected but not yet saved
                                          <div className="mt-2 small text-muted">
                                            <FaFileUpload className="me-1" /> Ready to upload: {documents.aadhaar.front.name}
                                          </div>
                                        )
                                      )}
                                      {/* Display upload error if any */}
                                      {uploadError.aadhaarFront && (
                                        <div className="mt-2 small text-danger">
                                          {uploadError.aadhaarFront}
                                        </div>
                                      )}
                                    </Form.Group>
                                  </Col>
                                  
                                  <Col xs={12}>
                                    <Form.Group>
                                      <Form.Label>Back Side</Form.Label>
                                      <InputGroup>
                                        <Form.Control 
                                          type="file" 
                                          onChange={(e) => handleDocumentUpload(e, 'aadhaar', 'back')}
                                          accept="image/*,.pdf"
                                          disabled={uploading.aadhaarBack}
                                          isInvalid={!!uploadError.aadhaarBack}
                                        />
                                        <Button variant="outline-primary" disabled={uploading.aadhaarBack}>
                                          {uploading.aadhaarBack ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                          ) : (
                                            <FaUpload className="me-1" />
                                          )}
                                          {uploading.aadhaarBack ? ' Uploading...' : ' Upload'}
                                        </Button>
                                        <Form.Control.Feedback type="invalid">
                                          {uploadError.aadhaarBack}
                                        </Form.Control.Feedback>
                                      </InputGroup>
                                      {/* Show uploaded file name or link to existing file */}
                                      {!uploading.aadhaarBack && !uploadError.aadhaarBack && (
                                        formData.adhaar_card_back_file ? (
                                          <div className="mt-2 small">
                                            <FaCheck className="me-1 text-success" />
                                            <a href={formData.adhaar_card_back_file} target="_blank" rel="noopener noreferrer">
                                              View Uploaded Back
                                            </a>
                                            <span className="text-muted ms-2">({documents.aadhaar.back?.name || 'Previously uploaded'})</span>
                                          </div>
                                        ) : documents.aadhaar.back && (
                                          // Show only if a new file was just selected but not yet saved
                                          <div className="mt-2 small text-muted">
                                            <FaFileUpload className="me-1" /> Ready to upload: {documents.aadhaar.back.name}
                                          </div>
                                        )
                                      )}
                                      {/* Display upload error if any */}
                                      {uploadError.aadhaarBack && (
                                        <div className="mt-2 small text-danger">
                                          {uploadError.aadhaarBack}
                                        </div>
                                      )}
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                          
                          {/* PAN Card Upload - Only for Individual */}
                          <Col md={6} className="mb-4">
                            <Card className="h-100 shadow-sm">
                              <Card.Header className="bg-light py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0 d-flex align-items-center">
                                    <FaIdCard className="me-2 text-warning" /> PAN Card
                                  </h6>
                                  {/* Check if file URL exists in formData (meaning it's saved) */}
                                  {formData.pan_card_file ? (
                                    /* Check local verified state (updated by API call) */
                                    documents.pan.verified ? (
                                      /* If verified, show badge and only Reset button */
                                      <div className="d-flex align-items-center">
                                        <Badge bg="success" className="me-2">Verified</Badge>
                                        <Button
                                          size="sm"
                                          variant="outline-danger" /* Use outline for reset */
                                          className="py-0 px-1"
                                          title="Reset Verification"
                                          onClick={() => handleDocumentVerify('pan', false)}
                                        >
                                          <FaTimesCircle />
                                        </Button>
                                      </div>
                                    ) : (
                                      /* If not verified, show Approve button */
                                      <div className="d-flex">
                                        <Button
                                          size="sm"
                                          variant="success"
                                          className="me-1 py-0 px-1"
                                          title="Approve PAN"
                                          onClick={() => handleDocumentVerify('pan', true)}
                                        >
                                          <FaCheck />
                                        </Button>
                                      </div>
                                    )
                                  ) : (
                                    /* If no file URL exists, show Not Uploaded */
                                    <Badge bg="secondary">Not Uploaded</Badge>
                                  )}
                                </div>
                              </Card.Header>
                              <Card.Body>
                                <Form.Group>
                                  <Form.Label>Upload PAN Card</Form.Label>
                                  <InputGroup>
                                    <Form.Control 
                                      type="file" 
                                      onChange={(e) => handleDocumentUpload(e, 'pan')}
                                      accept="image/*,.pdf"
                                      disabled={uploading.pan}
                                      isInvalid={!!uploadError.pan} // Show error state
                                    />
                                    <Button variant="outline-primary" disabled={uploading.pan}>
                                      {uploading.pan ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                      ) : (
                                        <FaUpload className="me-1" />
                                      )}
                                      {uploading.pan ? ' Uploading...' : ' Upload'}
                                    </Button>
                                    <Form.Control.Feedback type="invalid">
                                      {uploadError.pan}
                                    </Form.Control.Feedback>
                                  </InputGroup>
                                  {/* Show uploaded file name or link to existing file */}
                                  {!uploading.pan && !uploadError.pan && (
                                    formData.pan_card_file ? (
                                      <div className="mt-2 small">
                                        <FaCheck className="me-1 text-success" />
                                        <a href={formData.pan_card_file} target="_blank" rel="noopener noreferrer">
                                          View Uploaded PAN
                                        </a>
                                        <span className="text-muted ms-2">({documents.pan.file?.name || 'Previously uploaded'})</span>
                                      </div>
                                    ) : documents.pan.file && (
                                      // Show only if a new file was just selected but not yet saved
                                      <div className="mt-2 small text-muted">
                                        <FaFileUpload className="me-1" /> Ready to upload: {documents.pan.file.name}
                                      </div>
                                    )
                                  )}
                                  {/* Display upload error if any */}
                                  {uploadError.pan && (
                                    <div className="mt-2 small text-danger">
                                      {uploadError.pan}
                                    </div>
                                  )}
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                        </>
                      )}
                      
                      {/* Company Certificate Upload - Only for Business */}
                      {formData.applicantType === 'Business' && (
                        <>
                        <Col md={6} className="mb-4">
                          <Card className="h-100 shadow-sm">
                            <Card.Header className="bg-light py-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 d-flex align-items-center">
                                  <FaBuilding className="me-2 text-info" /> Company Certificate
                                </h6>
                                
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <Form.Group>
                                <Form.Label>Upload Company Certificate</Form.Label>
                                <InputGroup>
                                  <Form.Control 
                                    type="file" 
                                    onChange={(e) => handleDocumentUpload(e, 'companyCertificate')}
                                    accept="image/*,.pdf"
                                    disabled={uploading.companyCertificate}
                                    isInvalid={!!uploadError.companyCertificate} // Show error state
                                  />
                                  <Button variant="outline-primary" disabled={uploading.companyCertificate}>
                                    {uploading.companyCertificate ? (
                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                      <FaUpload className="me-1" />
                                    )}
                                    {uploading.companyCertificate ? ' Uploading...' : ' Upload'}
                                  </Button>
                                  <Form.Control.Feedback type="invalid">
                                    {uploadError.companyCertificate}
                                  </Form.Control.Feedback>
                                </InputGroup>
                                {/* Show uploaded file name or link to existing file */}
                                {!uploading.companyCertificate && !uploadError.companyCertificate && (
                                  formData.company_certificate_file ? (
                                    <div className="mt-2 small">
                                      <FaCheck className="me-1 text-success" />
                                      <a href={formData.company_certificate_file} target="_blank" rel="noopener noreferrer">
                                        View Uploaded Certificate
                                      </a>
                                      <span className="text-muted ms-2">({documents.companyCertificate.file?.name || 'Previously uploaded'})</span>
                                    </div>
                                  ) : documents.companyCertificate.file && (
                                    // Show only if a new file was just selected but not yet saved
                                    <div className="mt-2 small text-muted">
                                      <FaFileUpload className="me-1" /> Ready to upload: {documents.companyCertificate.file.name}
                                    </div>
                                  )
                                )}
                                {/* Display upload error if any */}
                                {uploadError.companyCertificate && (
                                  <div className="mt-2 small text-danger">
                                    {uploadError.companyCertificate}
                                  </div>
                                )}
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>

                        {/* Company PAN Card Upload - Only for Business */}
                        <Col md={6} className="mb-4">
                          <Card className="h-100 shadow-sm">
                            <Card.Header className="bg-light py-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 d-flex align-items-center">
                                  <FaIdCard className="me-2 text-warning" /> Company PAN Card
                                </h6>
                                {/* Check if file URL exists in formData (meaning it's saved) */}
                                {formData.company_pan_card_file ? (
                                  /* Check local verified state (updated by API call) */
                                  documents.companyPanCard.verified ? (
                                    /* If verified, show badge and only Reset button */
                                    <div className="d-flex align-items-center">
                                      <Badge bg="success" className="me-2">Verified</Badge>
                                      <Button
                                        size="sm"
                                        variant="outline-danger" /* Use outline for reset */
                                        className="py-0 px-1"
                                        title="Reset Verification"
                                        onClick={() => handleDocumentVerify('companyPanCard', false)}
                                      >
                                        <FaTimesCircle />
                                      </Button>
                                    </div>
                                  ) : (
                                    /* If not verified, show Approve button */
                                    <div className="d-flex">
                                      <span className='text-muted'>Verify Pan Card </span><Button
                                        size="sm"
                                        variant="secondary"
                                        className="me-1 py-0 px-2 ms-2"
                                        title="Verify Company PAN"
                                        onClick={() => handleDocumentVerify('companyPanCard', true)}
                                      >
                                        <FaCheck />
                                      </Button>
                                    </div>
                                  )
                                ) : (
                                  /* If no file URL exists, show Not Uploaded */
                                  <Badge bg="secondary">Not Uploaded</Badge>
                                )}
                              </div>
                            </Card.Header>
                            <Card.Body>
                              <Form.Group>
                                <Form.Label>Upload Company PAN Card</Form.Label>
                                <InputGroup>
                                  <Form.Control 
                                    type="file" 
                                    onChange={(e) => handleDocumentUpload(e, 'companyPanCard')}
                                    accept="image/*,.pdf"
                                    disabled={uploading.companyPanCard}
                                    isInvalid={!!uploadError.companyPanCard}
                                  />
                                  <Button variant="outline-primary" disabled={uploading.companyPanCard}>
                                    {uploading.companyPanCard ? (
                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                      <FaUpload className="me-1" />
                                    )}
                                    {uploading.companyPanCard ? ' Uploading...' : ' Upload'}
                                  </Button>
                                  <Form.Control.Feedback type="invalid">
                                    {uploadError.companyPanCard}
                                  </Form.Control.Feedback>
                                </InputGroup>
                                {/* Show uploaded file name or link to existing file */}
                                {!uploading.companyPanCard && !uploadError.companyPanCard && (
                                  formData.company_pan_card_file ? (
                                    <div className="mt-2 small">
                                      <FaCheck className="me-1 text-success" />
                                      <a href={formData.company_pan_card_file} target="_blank" rel="noopener noreferrer">
                                        View Uploaded Company PAN
                                      </a>
                                      <span className="text-muted ms-2">({documents.companyPanCard.file?.name || 'Previously uploaded'})</span>
                                    </div>
                                  ) : documents.companyPanCard.file && (
                                    // Show only if a new file was just selected but not yet saved
                                    <div className="mt-2 small text-muted">
                                      <FaFileUpload className="me-1" /> Ready to upload: {documents.companyPanCard.file.name}
                                    </div>
                                  )
                                )}
                                {/* Display upload error if any */}
                                {uploadError.companyPanCard && (
                                  <div className="mt-2 small text-danger">
                                    {uploadError.companyPanCard}
                                  </div>
                                )}
                              </Form.Group>
                            </Card.Body>
                          </Card>
                        </Col>
                        </>
                      )}
                      
                      {!formData.applicantType && (
                        <Col md={12}>
                          <Alert variant="info">
                            Please select an applicant type in the Details tab to view relevant document upload options.
                          </Alert>
                        </Col>
                      )}
                    </Row>

                  </Tab.Pane>

                  {/* Agents Tab Pane */}
                  <Tab.Pane eventKey="agents" onEnter={fetchAgentsList}>
                    <h6 className="section-header text-primary border-bottom">
                      <FaUsers className="me-2" /> Agents Mapped to {formData.name}
                    </h6>
                    {isLoadingAgents ? (
                      <div className="text-center my-4">Loading agents...</div>
                    ) : agentsError ? (
                      <Alert variant="danger">{agentsError}</Alert>
                    ) : agentsList.length > 0 ? (
                      <Table striped hover responsive size="sm" className="mt-3">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentsList.map((agent, index) => (
                            <tr key={agent.id}>
                              <td>{index + 1}</td>
                              <td>{agent.name}</td>
                              <td>{agent.email}</td>
                              <td>{agent.mobile}</td>
                              <td>
                                <Badge bg={agent.is_active ? 'success' : 'secondary'}>
                                  {agent.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-reset-agent-${agent.id}`}>
                                      Reset Agent Password
                                    </Tooltip>
                                  }
                                >
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="py-0 px-1"
                                    // title attribute removed
                                    onClick={() => openAgentResetModal(agent)}
                                  >
                                    <FaKey />
                                  </Button>
                                </OverlayTrigger>
                                {/* Add other agent actions if needed (e.g., view details, deactivate) */}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info" className="mt-3">
                        No agents are currently mapped to this DSO.
                      </Alert>
                    )}
                  </Tab.Pane>
                  
                </Tab.Content>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Tab.Container>
      
      {/* Verify DSO Modal */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Verify DSO</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to verify this DSO partner? This will mark them as verified in the system.</p>
          <div className="mb-3">
            <p className="mb-0 fw-bold">DSO Details:</p>
            <p className="mb-0 text-muted">Name: {formData.name}</p>
            <p className="mb-0 text-muted">Email: {formData.email}</p>
            <p className="mb-0 text-muted">Phone: {formData.phone}</p>
            <p className="mb-0 text-muted">
              Type: {formData.applicantType} 
              {formData.applicantType === 'Business' ? ` (${formData.businessType})` : 
              formData.applicantType === 'Individual' ? ` (${formData.individualType})` : ''}
            </p>
          </div>
          <Alert variant="warning" className="mb-0">
            <FaCheck className="me-2" /> This action cannot be reversed. Please verify all details before proceeding.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleVerifyDSO}>
            <FaCheck className="me-1" /> Confirm Verification
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Generate a new password for this DSO partner.</p>
          <p className="mb-0 text-muted">DSO Name: {formData.name}</p>
          
          <Form.Group className="mt-3">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  checkPasswordStrength(e.target.value);
                }}
                placeholder="Enter new password"
              />
              <Button 
                variant="outline-secondary"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </Button>
            </InputGroup>
            
            {newPassword && (
              <Form.Text>
                Password strength: {passwordStrength && (
                  <span className={`text-${getPasswordStrengthColor()} fw-bold`}>
                    {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                  </span>
                )}
              </Form.Text>
            )}
          </Form.Group>
          
          <div className="password-checklist mt-2">
            <div className={`d-flex align-items-center ${newPassword && newPassword.length >= 5 ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least 5 characters
            </div>
            <div className={`d-flex align-items-center ${newPassword && newPassword.match(/[A-Z]/) ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least one uppercase letter
            </div>
            <div className={`d-flex align-items-center ${newPassword && newPassword.match(/[a-z]/) ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least one lowercase letter
            </div>
            <div className={`d-flex align-items-center ${newPassword && newPassword.match(/[0-9]/) ? 'text-success' : 'text-muted'}`}>
              <span className="me-1">✓</span> At least one number
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleResetPassword}
            disabled={!newPassword || passwordStrength !== 'strong'}
          >
            <FaKey className="me-1" /> Reset Password
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Agent Upload Modal */}
      <Modal show={showAgentUploadModal} onHide={() => !isUploadingAgents && setShowAgentUploadModal(false)} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton={!isUploadingAgents}>
          <Modal.Title>
            <FaUsers className="me-2" /> Upload Agents for {formData.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Select a CSV file containing agent data.</p>
          <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light border rounded">
             <span className='small'>Required columns: name, mobile, email</span> {/* Add password if needed */}
             <Button
                 variant="link"
                 size="sm"
                 onClick={downloadTemplate}
                 className="p-0 d-flex align-items-center"
                 disabled={isUploadingAgents}
             >
                 <FaDownload className="me-1" /> Download Template
             </Button>
          </div>

          <Form.Group controlId="agentFile" className="mb-3">
            {/* <Form.Label>Agent CSV File</Form.Label> */}
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleAgentFileChange}
              isInvalid={!!agentUploadError}
              disabled={isUploadingAgents}
            />
            <Form.Control.Feedback type="invalid">
              {agentUploadError}
            </Form.Control.Feedback>
          </Form.Group>
          {selectedAgentFile && !agentUploadError && (
            <div className="text-success small d-flex align-items-center">
              <FaFileAlt className="me-1" /> Selected: {selectedAgentFile.name}
            </div>
          )}
          {/* Display general upload error message if it's just a string */}
          {agentUploadError && typeof agentUploadError === 'string' && (
             <Alert variant="danger" className="mt-3 py-2 small">{agentUploadError}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAgentUploadModal(false)} disabled={isUploadingAgents}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAgentUpload}
            disabled={!selectedAgentFile || isUploadingAgents}
          >
            {isUploadingAgents ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Uploading...
              </>
            ) : (
              <>
                <FaUpload className="me-1" /> Upload Agents
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Agent Reset Password Modal */}
      <Modal show={showAgentResetModal} onHide={() => setShowAgentResetModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Agent Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Generate a new password for agent:</p>
          <p className="mb-0 fw-bold">{selectedAgentForReset?.name}</p>
          <p className="mb-3 text-muted small">{selectedAgentForReset?.email}</p>

          <Form.Group className="mt-3">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewAgentPassword ? "text" : "password"}
                value={newAgentPassword}
                onChange={(e) => {
                  setNewAgentPassword(e.target.value);
                  checkAgentPasswordStrength(e.target.value); // Use the agent-specific checker
                }}
                placeholder="Enter new password"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowNewAgentPassword(!showNewAgentPassword)}
              >
                {showNewAgentPassword ? <FaEyeSlash /> : <FaEye />}
              </Button>
            </InputGroup>

            {newAgentPassword && (
              <Form.Text>
                Password strength: {agentPasswordStrength && (
                  <span className={`text-${getAgentPasswordStrengthColor()} fw-bold`}>
                    {agentPasswordStrength.charAt(0).toUpperCase() + agentPasswordStrength.slice(1)}
                  </span>
                )}
              </Form.Text>
            )}
          </Form.Group>

          <div className="password-checklist mt-2">
            {/* Reuse password checklist JSX, checking newAgentPassword and agentPasswordStrength */}
            <div className={`d-flex align-items-center ${newAgentPassword && newAgentPassword.length >= 5 ? 'text-success' : 'text-muted'}`}>✓ At least 5 characters</div>
            <div className={`d-flex align-items-center ${newAgentPassword && newAgentPassword.match(/[A-Z]/) ? 'text-success' : 'text-muted'}`}>✓ At least one uppercase letter</div>
            <div className={`d-flex align-items-center ${newAgentPassword && newAgentPassword.match(/[a-z]/) ? 'text-success' : 'text-muted'}`}>✓ At least one lowercase letter</div>
            <div className={`d-flex align-items-center ${newAgentPassword && newAgentPassword.match(/[0-9]/) ? 'text-success' : 'text-muted'}`}>✓ At least one number</div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAgentResetModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAgentPasswordReset}
            disabled={!newAgentPassword || agentPasswordStrength !== 'strong'}
          >
            <FaKey className="me-1" /> Reset Password
          </Button>
        </Modal.Footer>
      </Modal>

    </Container> // Existing closing tag
  );
};

export default EditDSO;
